import {
  WalletDeclinedError,
  WalletRejectedError,
} from './services/Wallet.service';

/**
 * Ciclo de vida del `attemptId` de la recarga (RET-1852 / RET-1860).
 *
 * El `attemptId` identifica la INTENCIÓN de cobro del cliente, no la petición.
 * Es el tercer eje de la llave de idempotencia (`topup:{ws}:{monto}:{attemptId}`),
 * y es lo único que hace que un reintento tras una respuesta perdida no cobre
 * dos veces: Stripe deduplica solo si la llave se repite.
 *
 * VIVE EN sessionStorage, NO EN MEMORIA. Ese es el arreglo de RET-1860. Un
 * `useRef` no sobrevive un reload, y el reload es una reacción normal justo
 * cuando la red se cuelga — o sea, exactamente en el momento que la llave estable
 * existe para cubrir. Sin persistir, la protección se cae precisamente donde más
 * se necesita.
 *
 * `sessionStorage` y no `localStorage` a propósito: la intención pertenece a la
 * PESTAÑA. Dos pestañas del mismo cliente son dos intenciones distintas, y
 * compartirles la llave haría que la segunda recarga se deduplique contra la
 * primera — un no-cobro silencioso.
 */

/** Genera un id nuevo. El fallback cubre contextos sin `crypto` (jsdom viejo). */
export function newTopUpAttemptId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return `topup-attempt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Cuánto vale una intención guardada antes de considerarse vieja.
 *
 * Existe porque persistir SIN caducidad reabre el caso que RET-1852 cerró: las
 * llaves de Stripe viven 24 h, así que una intención guardada hace una hora más
 * una recarga deliberada del mismo monto se deduplican entre sí — el cliente
 * cree que pagó y el saldo nunca se mueve.
 *
 * 15 minutos es holgado para lo que tiene que cubrir (colgarse, recargar la
 * página y reintentar: segundos, a lo sumo un par de minutos) y queda muy por
 * debajo de las 24 h del proveedor, que es el margen que importa.
 */
const ATTEMPT_TTL_MS = 15 * 60 * 1000;

/**
 * El ámbito de una intención. Cada tipo de recarga lleva su propia intención,
 * con su propia llave de almacenamiento, porque son cobros distintos: cerrar la
 * intención de la recarga única después de un 200 no puede cerrar la de la
 * recarga por saldo bajo que sigue con un 424 sin resolver, ni al revés
 * (RET-2425, KTD6).
 *
 * `one-off` conserva la llave de siempre (`wallet.topup.attempt.{ws}`): una
 * pestaña abierta durante el despliegue no pierde su intención.
 */
type tAttemptScope = 'one-off' | 'threshold' | 'threshold-retry';

const STORAGE_PREFIX: Record<tAttemptScope, string> = {
  'one-off': 'wallet.topup.attempt.',
  threshold: 'wallet.topup.threshold.attempt.',
  'threshold-retry': 'wallet.topup.threshold.retry.attempt.',
};

const storageKey = (scope: tAttemptScope, workspaceId: string) =>
  `${STORAGE_PREFIX[scope]}${workspaceId}`;

/**
 * `paymentMethodId` es la tarjeta con la que el intento salió la PRIMERA vez,
 * o `null` escrito explícito si salió sin tarjeta (RET-2428). Ver
 * `readOrCreateOneOffAttempt`. En el alcance `threshold-retry` es la tarjeta
 * de «Cambiar tarjeta» (RET-2426). Es el id público (`pm_…`), no un secreto.
 */
type StoredAttempt = {
  id: string;
  createdAt: number;
  paymentMethodId: string | null;
};

/**
 * `sessionStorage` puede lanzar —modo privado de Safari, cookies bloqueadas— y
 * puede no existir. Un fallo de almacenamiento NO debe tumbar un cobro: se
 * degrada a memoria del módulo, que es lo que había antes de RET-1860. Se pierde
 * la protección ante reload, no la funcionalidad.
 */
const memoryFallback = new Map<string, StoredAttempt>();

/**
 * SOLO para tests. `memoryFallback` es un Map de MÓDULO, así que sobrevive
 * entre los `it()` de un mismo archivo y `sessionStorage.clear()` no lo toca.
 */
export function resetAttemptFallbackForTests(): void {
  memoryFallback.clear();
}

function readStored(key: string): StoredAttempt | null {
  return readFromSession(key) ?? memoryFallback.get(key) ?? null;
}

/**
 * Lee solo de `sessionStorage`. Devuelve null si no hay nada, si está corrupto o
 * si el acceso lanza.
 *
 * Se separó del fallback por un defecto real, y la clave es que el modo privado
 * de Safari falla de forma ASIMÉTRICA: `setItem` LANZA, pero `getItem` devuelve
 * null sin lanzar. Cuando el fallback se consultaba solo desde el `catch` del
 * lector, ese `catch` nunca corría — así que lo escrito en memoria no se volvía
 * a leer nunca, y cada intento generaba una intención nueva.
 *
 * Eso era PEOR que el `useRef` que esta persistencia reemplazó: aquél al menos
 * era estable dentro de un mount. Con storage roto, el reintento tras una
 * respuesta perdida volvía a cobrar — exactamente lo que RET-1852 cierra.
 */
function readFromSession(key: string): StoredAttempt | null {
  try {
    const raw = sessionStorage.getItem(key);

    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredAttempt>;

    if (
      typeof parsed?.id !== 'string' ||
      typeof parsed?.createdAt !== 'number'
    ) {
      return null;
    }

    // Un intento guardado antes de RET-2428 no trae el campo, y salió sin
    // tarjeta: antes ningún cobro mandaba una.
    return {
      id: parsed.id,
      createdAt: parsed.createdAt,
      paymentMethodId:
        typeof parsed.paymentMethodId === 'string' && parsed.paymentMethodId
          ? parsed.paymentMethodId
          : null,
    };
  } catch {
    return null;
  }
}

function writeStored(key: string, attempt: StoredAttempt): void {
  memoryFallback.set(key, attempt);

  try {
    sessionStorage.setItem(key, JSON.stringify(attempt));
  } catch {
    // Ya quedó en memoria; el reload es lo único que se pierde.
  }
}

/**
 * Devuelve la intención vigente de este workspace, o abre una nueva.
 *
 * Reusa la guardada solo si no ha caducado. Un id vacío o un JSON corrupto se
 * tratan como ausencia: componer una llave con basura es peor que empezar de
 * cero, porque `topup:{ws}:{monto}:` colisiona con cualquier otro cobro igual.
 */
export function readOrCreateAttemptId(
  workspaceId: string,
  now: number = Date.now(),
): string {
  return readOrCreateScoped(
    { scope: 'one-off', workspaceId, paymentMethodId: null },
    now,
  ).id;
}

/**
 * La intención de la recarga única CON la tarjeta que lleva (RET-2428, RC-16).
 *
 * La tarjeta queda fijada al intento y no a la petición, para que un
 * reintento tras un desenlace desconocido repita el cobro en vez de cobrar
 * otra vez. Por eso la llave NO lleva la tarjeta: si la llevara, guardar una
 * tarjeta entre la respuesta perdida y el reintento, o cerrar y reabrir el
 * modal, daría una llave nueva para un cobro que quizá ya ocurrió, y
 * wallet-service abriría otra factura. Mientras el intento siga abierto, cada
 * reintento manda la tarjeta con la que salió la primera vez (o ninguna), sin
 * importar la que el modal indique ahora. Sólo un intento nuevo, después de un
 * 200 o un 402 que cerraron el anterior, toma la tarjeta indicada.
 */
export function readOrCreateOneOffAttempt(
  intent: { workspaceId: string; paymentMethodId: string | null },
  now: number = Date.now(),
): { id: string; paymentMethodId: string | null } {
  const { id, paymentMethodId } = readOrCreateScoped(
    { scope: 'one-off', ...intent },
    now,
  );

  return { id, paymentMethodId };
}

function readOrCreateScoped(
  target: {
    scope: tAttemptScope;
    workspaceId: string;
    paymentMethodId: string | null;
  },
  now: number,
): StoredAttempt {
  const { scope, workspaceId, paymentMethodId } = target;

  if (!workspaceId) {
    return { id: newTopUpAttemptId(), createdAt: now, paymentMethodId };
  }

  const key = storageKey(scope, workspaceId);
  const stored = readStored(key);
  const age = stored ? now - stored.createdAt : Number.NaN;

  // `age >= 0` no es paranoia: una entrada con fecha en el FUTURO da edad
  // negativa, y una comparación ingenua `age < TTL` la trataría como fresquísima
  // — reusando para siempre una intención en la que no se puede confiar. Pasa
  // con un cambio de hora del sistema, y pasó aquí con una entrada que
  // sobrevivió entre tests.
  if (stored?.id && age >= 0 && age < ATTEMPT_TTL_MS) {
    return stored;
  }

  const fresh = { id: newTopUpAttemptId(), createdAt: now, paymentMethodId };
  writeStored(key, fresh);

  return fresh;
}

/**
 * Cierra la intención. Se llama ante toda respuesta DEFINITIVA —200 y 402— y
 * nunca ante un desenlace desconocido.
 *
 * Esa asimetría es la regla completa, y la mitad del 402 la encontró JC con un
 * spike contra Stripe test mode: el proveedor cachea también el rechazo, así que
 * conservar la llave tras un 402 le repetiría al cliente un rechazo de hace
 * horas sin llegar nunca al banco. Solo el desenlace desconocido —"¿se cobró o
 * no?"— justifica conservarla.
 */
export function clearAttemptId(workspaceId: string): void {
  clearScoped('one-off', workspaceId);
}

function clearScoped(scope: tAttemptScope, workspaceId: string): void {
  const key = storageKey(scope, workspaceId);

  memoryFallback.delete(key);

  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nada que hacer: ya se limpió la memoria.
  }
}

// ── Recarga por saldo bajo (RET-2425, KTD6) ─────────────────────────────────

/**
 * Devuelve la intención vigente del PUT de la recarga por saldo bajo, o abre
 * una nueva. Misma política que `readOrCreateAttemptId` (sessionStorage con
 * respaldo en memoria, 15 minutos), en su propio ámbito.
 *
 * La llave que viaja es `threshold:{ws}:{threshold}:{amount}:{attemptId}`
 * (`thresholdIdempotencyKeyFor`, Wallet.service.ts). Cambiar los valores
 * cambia la llave aunque la intención sea la misma, porque wallet rechaza una
 * llave reusada con otros valores. Reenviar los mismos valores con la
 * intención abierta repite la llave, y wallet responde lo mismo sin cobrar
 * otra vez.
 */
export function readOrCreateThresholdAttemptId(
  workspaceId: string,
  now: number = Date.now(),
): string {
  return readOrCreateScoped(
    { scope: 'threshold', workspaceId, paymentMethodId: null },
    now,
  ).id;
}

/**
 * Cierra la intención de la recarga por saldo bajo. Quien la llama decide
 * cuándo: con `closesThresholdAttempt` para la respuesta directa del PUT, y
 * después de la relectura de un 424 cuando el GET ya muestra los valores
 * pedidos (KTD7). Cerrarla al recuperarse evita una repetición: con la llave
 * viva, wallet respondería 200 a un reenvío con los mismos valores sin
 * escribir nada.
 */
export function clearThresholdAttemptId(workspaceId: string): void {
  clearScoped('threshold', workspaceId);
}

/**
 * Si la respuesta DIRECTA del PUT cierra la intención (KTD6).
 *
 * `null` es el 200. Cierran el 200, el 402 (`WalletDeclinedError`: el
 * proveedor también cachea el rechazo, ver `clearAttemptId`) y el 400
 * (`WalletRejectedError` con status 400: nada se cobró y nada se guardó).
 *
 * Todo lo demás la deja ABIERTA, para que el reenvío use la misma llave: el
 * 424 y la red caída (no se sabe si hubo cobro), el 409 (puede llegar después
 * del cobro, y el reemplazo que sigue manda los mismos valores), el 403 y
 * cualquier error sin clasificar. El 424 cuya relectura muestra los valores
 * guardados no pasa por aquí: lo cierra el contenedor con
 * `clearThresholdAttemptId`.
 */
export function closesThresholdAttempt(error: unknown): boolean {
  if (error === null) return true;
  if (error instanceof WalletDeclinedError) return true;

  return error instanceof WalletRejectedError && error.status === 400;
}

// ── Reintento de la recarga por saldo bajo (RET-2426, KTD7) ──────────────────

/** La intención vigente del reintento: su id y la tarjeta atada a ella, si hay. */
export type tThresholdRetryAttempt = {
  id: string;
  /** Solo en «Cambiar tarjeta»; null en «Reintentar el cobro». */
  paymentMethodId: string | null;
};

/**
 * Devuelve la intención vigente del reintento de la pausa
 * (`POST …/topup/threshold/retry`), o abre una nueva. Misma política que las
 * otras dos (sessionStorage con respaldo en memoria, 15 minutos), en su propio
 * ámbito: el reintento no comparte llave con el PUT ni con la recarga única.
 *
 * La llave que viaja es `threshold-retry:{ws}:{id}`. Con la misma llave y la
 * misma tarjeta wallet responde lo mismo sin cobrar otra vez, que es lo que
 * cubre un 424 o un remontaje: por eso la tarjeta del «Cambiar tarjeta» vive
 * AQUÍ, junto al id. Si solo viviera en el estado del componente, un remontaje
 * conservaría la llave y perdería la tarjeta, y el reenvío saldría sin
 * `paymentMethodId`: cobraría la tarjeta vieja bajo la misma llave.
 */
export function readOrCreateThresholdRetryAttempt(
  workspaceId: string,
  now: number = Date.now(),
): tThresholdRetryAttempt {
  const attempt = readOrCreateScoped(
    { scope: 'threshold-retry', workspaceId, paymentMethodId: null },
    now,
  );

  return { id: attempt.id, paymentMethodId: attempt.paymentMethodId };
}

/**
 * Ata la tarjeta de «Cambiar tarjeta» a la intención vigente del reintento y
 * devuelve la intención resultante.
 *
 * La misma tarjeta no cambia nada. Una tarjeta DISTINTA a la ya guardada abre
 * una intención nueva: es otro cobro, y repetir la llave de un cobro con otra
 * tarjeta es justo lo que la llave existe para impedir. Atarla a una intención
 * que todavía no tenía tarjeta («Reintentar» sin resolver) conserva el id.
 * Conserva `createdAt`: guardar la tarjeta no alarga los 15 minutos.
 */
export function setThresholdRetryPaymentMethod(
  target: { workspaceId: string; paymentMethodId: string },
  now: number = Date.now(),
): tThresholdRetryAttempt {
  const { workspaceId, paymentMethodId } = target;
  const current = readOrCreateScoped(
    { scope: 'threshold-retry', workspaceId, paymentMethodId: null },
    now,
  );

  if (!workspaceId || !paymentMethodId) {
    return { id: current.id, paymentMethodId: null };
  }

  const key = storageKey('threshold-retry', workspaceId);
  const keepsAttempt =
    current.paymentMethodId === null ||
    current.paymentMethodId === paymentMethodId;
  const next: StoredAttempt = keepsAttempt
    ? { ...current, paymentMethodId }
    : { id: newTopUpAttemptId(), createdAt: now, paymentMethodId };

  writeStored(key, next);

  return { id: next.id, paymentMethodId };
}

/**
 * Cierra la intención del reintento. Quien la llama decide cuándo (KTD7): una
 * respuesta definitiva la cierra (402, 409, o un 200 `charged`/`pending` cuya
 * pausa ya terminó); `requires_action` no es definitivo y la deja abierta hasta
 * que la relectura muestre la pausa terminada, y un 424 la deja abierta para
 * reenviar con la misma llave y la misma tarjeta.
 */
export function clearThresholdRetryAttempt(workspaceId: string): void {
  clearScoped('threshold-retry', workspaceId);
}
